import { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Loader, XCircle } from "lucide-react";
import {
  getInvestigationSales,
  updateInvestigationSale,
  bulkUpdateInvestigationSales,
  UpdateInvestigationSaleProps,
  BulkUpdateInvestigationSaleItem,
} from "@/Api/sales";
import { Mask } from "@/utils/mask";
import { InvestigationSaleProps } from "@/pages/InvestigationSales";
import { ScrollArea } from "@/components/ui/scroll-area";
import { usePermissions } from "@/hooks/usePermissions";
import { TypeUser } from "@/types/userType";

interface DetailModalProps {
  contestId: number | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => Promise<void>;
  selectedStatus?: number;
}

interface ContestDetails {
  contest: {
    id: number;
    uuid: string;
    name: string;
    validation_date?: string;
  };
  lottery: {
    id: number;
    name: string;
    color: string;
  };
  investigations: InvestigationSaleProps[];
}

export const DetailModal = ({
  contestId,
  isOpen,
  onClose,
  onSuccess,
  selectedStatus = 1,
}: DetailModalProps) => {
  const { user } = usePermissions();
  // Vendedor só consulta a apuração, sem nenhuma ação de alteração
  const isReadOnly = user?.user_type_id === TypeUser.seller;
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<ContestDetails | null>(null);
  const [formStates, setFormStates] = useState<Record<string, {
    investigation_price_total: string;
    investigation_price_per_share: string;
    receive_by_cashier: boolean;
    non_winning_ticket: boolean;
    isEditing: boolean;
  }>>({});
  const [savingId, setSavingId] = useState<string | null>(null);
  const [savingAll, setSavingAll] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [confirmSingleModal, setConfirmSingleModal] = useState<{
    isOpen: boolean;
    investigation: InvestigationSaleProps | null;
  }>({ isOpen: false, investigation: null });

  const fetchDetails = useCallback(async () => {
    if (!contestId) return;

    setLoading(true);
    try {
      const { data: response } = await getInvestigationSales(1, {
        contest_status_id: selectedStatus,
        contest_id: contestId,
      });

      const apiResponse = response?.response;
      const investigations = apiResponse?.investigations || [];
      const contest = apiResponse?.contest;
      const lottery = apiResponse?.lottery;

      if (contest && lottery) {
        setData({
          contest: {
            id: contest.id || contestId,
            uuid: contest.uuid || "",
            name: contest.name || "Concurso",
            validation_date: contest.validation_date,
          },
          lottery: {
            id: lottery.id || 0,
            name: lottery.name || "Loteria",
            color: lottery.color || "#023859",
          },
          investigations,
        });

        const newFormStates: Record<string, any> = {};
        investigations.forEach((inv: InvestigationSaleProps) => {
          const totalValue = parseFloat(String(inv.investigation_price_total || "0"));
          const perShareValue = parseFloat(String(inv.investigation_price_per_share || "0"));
          const isNonWinning = checkNonWinning(inv.non_winning_ticket);
          const isDone = inv.author_id !== null && inv.author_id !== undefined;

          newFormStates[inv.uuid] = {
            investigation_price_total: Mask.formatCurrency(totalValue),
            investigation_price_per_share: Mask.formatCurrency(perShareValue),
            receive_by_cashier: inv.receive_by_cashier || false,
            non_winning_ticket: isNonWinning,
            isEditing: !isDone,
          };
        });
        setFormStates(newFormStates);
      }
    } catch (error) {
      console.error("Erro ao buscar detalhes:", error);
      toast.error("Erro ao carregar detalhes da apuração");
    } finally {
      setLoading(false);
    }
  }, [contestId, selectedStatus]);

  useEffect(() => {
    if (isOpen && contestId) {
      fetchDetails();
    }
    if (!isOpen) {
      setData(null);
      setFormStates({});
    }
  }, [isOpen, contestId, fetchDetails]);

  const getQuotaQuantity = (investigation: InvestigationSaleProps): number => {
    return investigation.product_classification?.quota_quantity || 0;
  };

  const getProductInfo = (investigation: InvestigationSaleProps): string => {
    const pc = investigation.product_classification;
    if (!pc) return "Produto";

    const productName = investigation.contest_product_classification?.product_name || "Bolão";
    const games = pc.game_quantity || 0;
    const dezenas = pc.dez_quantity || "0";
    const cotas = pc.quota_quantity || 0;

    return `${productName} - ${games}jg X ${dezenas}d X ${cotas}ct`;
  };

  const calculatePricePerShare = (totalValue: number, quotaQuantity: number): number => {
    return quotaQuantity > 0 ? totalValue / quotaQuantity : 0;
  };

  const handleTotalChange = (uuid: string, value: string, quotaQuantity: number) => {
    const totalValue = parseFloat(Mask.transformCurrencyInFloat(value)) || 0;
    const pricePerShare = calculatePricePerShare(totalValue, quotaQuantity);

    setFormStates(prev => ({
      ...prev,
      [uuid]: {
        ...prev[uuid],
        investigation_price_total: value,
        investigation_price_per_share: Mask.formatCurrency(pricePerShare),
      }
    }));
  };

  const handlePerShareChange = (uuid: string, value: string, quotaQuantity: number) => {
    const perShareValue = parseFloat(Mask.transformCurrencyInFloat(value)) || 0;
    const totalValue = perShareValue * quotaQuantity;

    setFormStates(prev => ({
      ...prev,
      [uuid]: {
        ...prev[uuid],
        investigation_price_per_share: value,
        investigation_price_total: Mask.formatCurrency(totalValue),
      }
    }));
  };

  const handleCheckboxChange = (uuid: string, checked: boolean) => {
    setFormStates(prev => ({
      ...prev,
      [uuid]: {
        ...prev[uuid],
        receive_by_cashier: checked,
      }
    }));
  };

  const handleNonWinningChange = (uuid: string, checked: boolean) => {
    setFormStates(prev => ({
      ...prev,
      [uuid]: {
        ...prev[uuid],
        non_winning_ticket: checked,
        investigation_price_total: checked ? "R$ 0,00" : prev[uuid]?.investigation_price_total || "R$ 0,00",
        investigation_price_per_share: checked ? "R$ 0,00" : prev[uuid]?.investigation_price_per_share || "R$ 0,00",
      }
    }));
  };

  const handleEdit = (uuid: string) => {
    setFormStates(prev => ({
      ...prev,
      [uuid]: {
        ...prev[uuid],
        isEditing: true,
      }
    }));
  };

  const handleCancel = (uuid: string, investigation: InvestigationSaleProps) => {
    const totalValue = parseFloat(String(investigation.investigation_price_total || "0"));
    const perShareValue = parseFloat(String(investigation.investigation_price_per_share || "0"));
    const isNonWinning = checkNonWinning(investigation.non_winning_ticket);
    const isDone = investigation.author_id !== null && investigation.author_id !== undefined;

    setFormStates(prev => ({
      ...prev,
      [uuid]: {
        investigation_price_total: Mask.formatCurrency(totalValue),
        investigation_price_per_share: Mask.formatCurrency(perShareValue),
        receive_by_cashier: investigation.receive_by_cashier || false,
        non_winning_ticket: isNonWinning,
        isEditing: !isDone,
      }
    }));
  };

  const handleSave = async (investigation: InvestigationSaleProps) => {
    const state = formStates[investigation.uuid];
    if (!state) return;

    setSavingId(investigation.uuid);
    try {
      const totalValue = state.non_winning_ticket ? 0 : (parseFloat(Mask.transformCurrencyInFloat(state.investigation_price_total)) || 0);
      const pricePerShare = state.non_winning_ticket ? 0 : (parseFloat(Mask.transformCurrencyInFloat(state.investigation_price_per_share)) || 0);

      const updateData: UpdateInvestigationSaleProps = {
        investigation_price_total: totalValue,
        investigation_price_per_share: pricePerShare,
        receive_by_cashier: state.receive_by_cashier,
        non_winning_ticket: state.non_winning_ticket,
      };

      const response = await updateInvestigationSale(investigation.uuid, updateData);

      if (response.data?.status !== false) {
        toast.success("Apuração salva com sucesso!");
        await fetchDetails();
        onSuccess();
      } else {
        toast.error(response.data?.message || "Erro ao salvar apuração");
      }
    } catch (error) {
      console.error("Erro ao salvar:", error);
      toast.error("Erro ao salvar apuração");
    } finally {
      setSavingId(null);
    }
  };

  const checkNonWinning = (value: unknown): boolean => {
    return value === true || value === 1 || value === "1";
  };

  const handleSaveAll = async () => {
    if (!data?.investigations) return;

    const pendingInvestigations = data.investigations.filter(inv => {
      const isDone = inv.author_id !== null && inv.author_id !== undefined;
      return !isDone;
    });

    if (pendingInvestigations.length === 0) {
      toast.info("Todas as apurações já foram realizadas!");
      return;
    }

    setSavingAll(true);

    try {
      // Monta array para bulk update
      const investigationsToUpdate: BulkUpdateInvestigationSaleItem[] = pendingInvestigations
        .map(investigation => {
          const state = formStates[investigation.uuid];
          if (!state) return null;

          const totalValue = state.non_winning_ticket ? 0 : (parseFloat(Mask.transformCurrencyInFloat(state.investigation_price_total)) || 0);
          const pricePerShare = state.non_winning_ticket ? 0 : (parseFloat(Mask.transformCurrencyInFloat(state.investigation_price_per_share)) || 0);

          return {
            uuid: investigation.uuid,
            investigation_price_total: totalValue,
            investigation_price_per_share: pricePerShare,
            receive_by_cashier: state.receive_by_cashier,
            non_winning_ticket: state.non_winning_ticket,
          };
        })
        .filter((item): item is BulkUpdateInvestigationSaleItem => item !== null);

      if (investigationsToUpdate.length === 0) {
        toast.info("Nenhuma apuração para processar.");
        setSavingAll(false);
        return;
      }

      // Faz uma única chamada de API para todas as investigações
      const response = await bulkUpdateInvestigationSales(investigationsToUpdate);

      if (response.data?.status !== false) {
        const result = response.data?.response;
        const successCount = result?.total_processed || investigationsToUpdate.length;
        const errorCount = result?.total_errors || 0;

        if (successCount > 0) {
          toast.success(`${successCount} apuração(ões) realizada(s) com sucesso!`);
          await fetchDetails();
          onSuccess();
        }
        if (errorCount > 0) {
          toast.error(`${errorCount} apuração(ões) falharam.`);
        }
      } else {
        toast.error(response.data?.message || "Erro ao processar apurações");
      }
    } catch (error) {
      console.error("Erro ao salvar:", error);
      toast.error("Erro ao processar apurações em lote");
    }

    setSavingAll(false);
  };

  const isInvestigationDone = (investigation: InvestigationSaleProps): boolean => {
    return investigation.author_id !== null && investigation.author_id !== undefined;
  };

  const getApuratedCount = () => {
    if (!data?.investigations) return 0;
    return data.investigations.filter(i => isInvestigationDone(i)).length;
  };

  const getPendingCount = () => {
    if (!data?.investigations) return 0;
    return data.investigations.filter(i => !isInvestigationDone(i)).length;
  };

  const getPendingInvestigations = () => {
    if (!data?.investigations) return [];
    return data.investigations.filter(i => !isInvestigationDone(i));
  };

  const handleOpenConfirmModal = () => {
    if (getPendingCount() === 0) {
      toast.info("Todas as apurações já foram realizadas!");
      return;
    }
    setConfirmModalOpen(true);
  };

  const handleConfirmSaveAll = async () => {
    setConfirmModalOpen(false);
    await handleSaveAll();
  };

  const handleOpenSingleConfirm = (investigation: InvestigationSaleProps) => {
    setConfirmSingleModal({ isOpen: true, investigation });
  };

  const handleConfirmSingleSave = async () => {
    if (confirmSingleModal.investigation) {
      setConfirmSingleModal({ isOpen: false, investigation: null });
      await handleSave(confirmSingleModal.investigation);
    }
  };

  return (
    <>
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[95vw] lg:max-w-[1200px] max-h-[90vh] p-0">
        <DialogHeader
          className="p-4 text-white rounded-t-lg"
          style={{ backgroundColor: data?.lottery.color || "#023859" }}
        >
          <div className="flex items-center justify-between">
            <DialogTitle className="text-xl font-bold flex items-center gap-3">
              <div
                className="w-6 h-6 rounded-full bg-white/30"
              />
              {data?.lottery.name || "Loteria"} - {data?.contest.name || "Concurso"}
            </DialogTitle>
            {data?.contest.validation_date && (
              <span className="text-sm font-medium bg-white/20 px-3 py-1 rounded">
                Data de Validade: {Mask.transformMaskBirthdayInBr(data.contest.validation_date)}
              </span>
            )}
          </div>
        </DialogHeader>

        <ScrollArea className="h-[65vh] px-6">
          {loading ? (
            <div className="flex justify-center items-center h-40">
              <Loader className="animate-spin h-8 w-8 text-blue-600" />
            </div>
          ) : data?.investigations && data.investigations.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 py-4">
              {data.investigations.map((investigation) => {
                const quotaQuantity = getQuotaQuantity(investigation);
                const state = formStates[investigation.uuid];
                const isSaving = savingId === investigation.uuid;
                const isDone = isInvestigationDone(investigation);
                const productInfo = getProductInfo(investigation);

                if ((isDone && !state?.isEditing) || isReadOnly) {
                  const totalValue = parseFloat(String(investigation.investigation_price_total || "0"));
                  const perShareValue = parseFloat(String(investigation.investigation_price_per_share || "0"));

                  return (
                    <div
                      key={investigation.uuid}
                      className="border rounded-lg overflow-hidden bg-white"
                    >
                      <div
                        className="px-4 py-3"
                        style={{ backgroundColor: data?.lottery.color || "#023859" }}
                      >
                        <span className="text-sm font-bold text-white">
                          {productInfo}
                        </span>
                      </div>

                      <div className="p-4 space-y-3">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">Valor por cota:</span>
                          <span className="font-semibold text-gray-900">
                            {Mask.formatCurrency(perShareValue)}
                          </span>
                        </div>

                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">Valor total:</span>
                          <span className="font-semibold text-gray-900">
                            {Mask.formatCurrency(totalValue)}
                          </span>
                        </div>

                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">Cotas:</span>
                          <span className="font-semibold text-gray-900">
                            {quotaQuantity}
                          </span>
                        </div>

                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">Receber pelo caixa:</span>
                          <span className={`px-2 py-1 rounded text-xs font-medium flex items-center gap-1 ${
                            investigation.receive_by_cashier
                              ? "bg-green-100 text-green-700"
                              : "bg-red-100 text-red-700"
                          }`}>
                            {investigation.receive_by_cashier ? (
                              "Sim"
                            ) : (
                              <>
                                <XCircle className="w-3 h-3" />
                                Não
                              </>
                            )}
                          </span>
                        </div>

                        {isDone ? (
                          <>
                            <div className="bg-green-100 text-green-700 text-center py-2 rounded font-medium mt-4">
                              Apuração Realizada
                            </div>

                            {!isReadOnly && (
                              <Button
                                variant="outline"
                                onClick={() => handleEdit(investigation.uuid)}
                                className="w-full"
                              >
                                Editar apuração
                              </Button>
                            )}
                          </>
                        ) : (
                          <div className="bg-yellow-100 text-yellow-700 text-center py-2 rounded font-medium mt-4">
                            Aguardando apuração
                          </div>
                        )}
                      </div>
                    </div>
                  );
                }

                return (
                  <div
                    key={investigation.uuid}
                    className="border rounded-lg overflow-hidden bg-white"
                  >
                    <div
                      className="px-4 py-3"
                      style={{ backgroundColor: data?.lottery.color || "#023859" }}
                    >
                      <span className="text-sm font-bold text-white">
                        {productInfo}
                      </span>
                    </div>

                    <div className="p-4 space-y-4">
                      <div>
                        <label className="text-sm text-gray-600 block mb-1">
                          Valor total da apuração:
                        </label>
                        <Input
                          typeMask="currency"
                          value={state?.non_winning_ticket ? "R$ 0,00" : (state?.investigation_price_total || "R$ 0,00")}
                          onChange={(e) => handleTotalChange(investigation.uuid, e.target.value, quotaQuantity)}
                          disabled={isSaving || state?.non_winning_ticket}
                          className="h-10"
                        />
                      </div>

                      <div>
                        <label className="text-sm text-gray-600 block mb-1">
                          Valor por cota ({quotaQuantity} cotas):
                        </label>
                        <Input
                          typeMask="currency"
                          value={state?.non_winning_ticket ? "R$ 0,00" : (state?.investigation_price_per_share || "R$ 0,00")}
                          onChange={(e) => handlePerShareChange(investigation.uuid, e.target.value, quotaQuantity)}
                          disabled={isSaving || state?.non_winning_ticket}
                          className="h-10"
                        />
                      </div>

                      <p className="text-xs text-gray-500">
                        Você pode alterar este valor manualmente
                      </p>

                      <label className="flex items-center gap-2 cursor-pointer">
                        <Checkbox
                          checked={state?.receive_by_cashier || false}
                          onCheckedChange={(checked) =>
                            handleCheckboxChange(investigation.uuid, Boolean(checked))
                          }
                          disabled={isSaving || state?.non_winning_ticket}
                        />
                        <span className="text-sm text-gray-700">Valor recebido pelo caixa</span>
                      </label>

                      <label className="flex items-center gap-2 cursor-pointer">
                        <Checkbox
                          checked={state?.non_winning_ticket || false}
                          onCheckedChange={(checked) =>
                            handleNonWinningChange(investigation.uuid, Boolean(checked))
                          }
                          disabled={isSaving}
                        />
                        <span className="text-sm text-gray-700">BILHETE NÃO PREMIADO</span>
                      </label>

                      <div className="flex gap-2 pt-2">
                        <Button
                          variant="outline"
                          onClick={() => handleCancel(investigation.uuid, investigation)}
                          disabled={isSaving}
                          className="flex-1"
                        >
                          Cancelar
                        </Button>
                        <Button
                          onClick={() => handleOpenSingleConfirm(investigation)}
                          disabled={isSaving}
                          className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                        >
                          {isSaving ? (
                            <Loader className="h-4 w-4 animate-spin" />
                          ) : (
                            "Fazer Apuração"
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              Nenhum produto encontrado para apuração.
            </div>
          )}
        </ScrollArea>

        <div className="p-4 border-t flex items-center justify-between bg-gray-50">
          <div className="text-sm text-gray-600">
            {data && (
              <>
                Página 1 de 1 | {" "}
                <span className="font-medium">Total:</span> {data.investigations.length} |{" "}
                <span className="font-medium text-green-600">Apurados:</span> {getApuratedCount()}
              </>
            )}
          </div>
          <div className="flex gap-2">
            {!isReadOnly && getPendingCount() > 0 && (
              <Button
                onClick={handleOpenConfirmModal}
                disabled={savingAll}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                {savingAll ? (
                  <>
                    <Loader className="h-4 w-4 animate-spin mr-2" />
                    Apurando...
                  </>
                ) : (
                  `Apurar Tudo (${getPendingCount()})`
                )}
              </Button>
            )}
            <Button variant="outline" onClick={onClose}>
              Fechar
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>

      <Dialog open={confirmModalOpen} onOpenChange={setConfirmModalOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Confirmar Apuração em Massa</DialogTitle>
            <DialogDescription>
              Você está prestes a realizar a apuração de todos os produtos pendentes.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm font-medium text-yellow-800 mb-2">
                Total de produtos a serem apurados: <span className="text-lg font-bold">{getPendingCount()}</span>
              </p>
            </div>

            <div className="space-y-2">
              <p className="text-sm font-medium text-gray-700">Produtos pendentes:</p>
              <ScrollArea className="h-[200px] border rounded-lg p-3">
                <div className="space-y-2">
                  {getPendingInvestigations().map((inv) => {
                    const state = formStates[inv.uuid];
                    return (
                      <div
                        key={inv.uuid}
                        className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm"
                      >
                        <span className="font-medium text-gray-700">
                          {getProductInfo(inv)}
                        </span>
                        <span className={`text-xs px-2 py-1 rounded ${
                          state?.non_winning_ticket
                            ? "bg-red-100 text-red-700"
                            : "bg-green-100 text-green-700"
                        }`}>
                          {state?.non_winning_ticket
                            ? "Não Premiado"
                            : state?.investigation_price_total || "R$ 0,00"
                          }
                        </span>
                      </div>
                    );
                  })}
                </div>
              </ScrollArea>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmModalOpen(false)}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleConfirmSaveAll}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              Confirmar Apuração
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={confirmSingleModal.isOpen}
        onOpenChange={(open) => !open && setConfirmSingleModal({ isOpen: false, investigation: null })}
      >
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle>Confirmar Apuração</DialogTitle>
            <DialogDescription>
              Confirme os dados da apuração antes de salvar.
            </DialogDescription>
          </DialogHeader>

          {confirmSingleModal.investigation && (
            <div className="space-y-4">
              <div
                className="p-3 rounded-lg text-white font-medium"
                style={{ backgroundColor: data?.lottery.color || "#023859" }}
              >
                {getProductInfo(confirmSingleModal.investigation)}
              </div>

              <div className="space-y-3 p-4 bg-gray-50 rounded-lg">
                {(() => {
                  const state = formStates[confirmSingleModal.investigation.uuid];
                  const quotaQuantity = getQuotaQuantity(confirmSingleModal.investigation);

                  return (
                    <>
                      {state?.non_winning_ticket ? (
                        <div className="flex items-center justify-center p-3 bg-red-100 rounded-lg">
                          <span className="text-red-700 font-medium">BILHETE NÃO PREMIADO</span>
                        </div>
                      ) : (
                        <>
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">Valor total:</span>
                            <span className="font-semibold text-gray-900">
                              {state?.investigation_price_total || "R$ 0,00"}
                            </span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">Valor por cota:</span>
                            <span className="font-semibold text-gray-900">
                              {state?.investigation_price_per_share || "R$ 0,00"}
                            </span>
                          </div>
                        </>
                      )}
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Cotas:</span>
                        <span className="font-semibold text-gray-900">{quotaQuantity}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Receber pelo caixa:</span>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          state?.receive_by_cashier
                            ? "bg-green-100 text-green-700"
                            : "bg-red-100 text-red-700"
                        }`}>
                          {state?.receive_by_cashier ? "Sim" : "Não"}
                        </span>
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmSingleModal({ isOpen: false, investigation: null })}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleConfirmSingleSave}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              Confirmar Apuração
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
